/**
 * RawMessageView Component
 *
 * Debug view for showing message structure as JSON with syntax highlighting.
 * Used when raw mode is enabled in the StatusBar.
 *
 * Features:
 * - JSON syntax highlighting using Prism
 * - Click to copy entire message
 * - Collapsed by default for large messages (>5KB)
 * - Separate blocks for different content types
 *
 * @module components/session/RawMessageView
 */

import { memo, useState, useCallback } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, ChevronDown, ChevronRight } from 'lucide-react';

// Size threshold for auto-collapse (5KB)
const COLLAPSE_THRESHOLD = 5 * 1024;

/**
 * Individual JSON block with copy functionality
 */
const RawBlock = memo(function RawBlock({ data, label, defaultCollapsed = false }) {
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const jsonString = JSON.stringify(data, null, 2);
  const isLarge = jsonString.length > COLLAPSE_THRESHOLD;

  const handleCopy = useCallback(async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [jsonString]);

  const toggleCollapse = useCallback(() => {
    setCollapsed(prev => !prev);
  }, []);

  // Auto-collapse large blocks on initial render
  const shouldCollapse = collapsed || (isLarge && defaultCollapsed);

  return (
    <div className="relative group mb-2">
      {/* Header with label and controls */}
      <div
        className="flex items-center justify-between px-2 py-1 bg-gray-800 rounded-t border-b border-gray-700 cursor-pointer hover:bg-gray-750"
        onClick={toggleCollapse}
      >
        <div className="flex items-center gap-2">
          {shouldCollapse ? (
            <ChevronRight className="w-3 h-3 text-gray-400" />
          ) : (
            <ChevronDown className="w-3 h-3 text-gray-400" />
          )}
          <span className="text-xs text-gray-400 font-mono">{label}</span>
          {isLarge && (
            <span className="text-[10px] text-gray-500">
              ({(jsonString.length / 1024).toFixed(1)}KB)
            </span>
          )}
        </div>
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-gray-700 transition-colors"
          title="Copy JSON"
        >
          {copied ? (
            <Check className="w-3 h-3 text-green-400" />
          ) : (
            <Copy className="w-3 h-3 text-gray-400 group-hover:text-gray-300" />
          )}
        </button>
      </div>

      {/* JSON content */}
      {!shouldCollapse && (
        <div
          className="cursor-pointer"
          onClick={handleCopy}
          title="Click to copy"
        >
          <SyntaxHighlighter
            language="json"
            style={oneDark}
            customStyle={{
              margin: 0,
              borderRadius: '0 0 0.375rem 0.375rem',
              fontSize: '11px',
              maxHeight: '400px',
              overflow: 'auto',
            }}
          >
            {jsonString}
          </SyntaxHighlighter>
        </div>
      )}

      {/* Collapsed preview */}
      {shouldCollapse && (
        <div
          className="px-3 py-2 bg-gray-900 rounded-b text-gray-500 text-xs font-mono cursor-pointer hover:bg-gray-850"
          onClick={toggleCollapse}
        >
          {jsonString.slice(0, 100)}...
        </div>
      )}
    </div>
  );
});

/**
 * Main RawMessageView component
 */
function RawMessageView({ message }) {
  if (!message) return null;

  const { type, content, tool, input, toolUseId, isError, attachments, ...rest } = message;

  // Determine label based on message type
  const getLabel = () => {
    switch (type) {
      case 'user':
        return 'user_message';
      case 'assistant':
        return 'assistant_message';
      case 'tool_use':
        return `tool_use: ${tool}`;
      case 'tool_result':
        return `tool_result${isError ? ' (error)' : ''}`;
      case 'system':
      case 'system_response':
        return 'system';
      case 'error':
        return 'error';
      default:
        return type || 'unknown';
    }
  };

  // Check if content is large (for auto-collapse)
  const contentSize = JSON.stringify(message).length;
  const isLarge = contentSize > COLLAPSE_THRESHOLD;

  return (
    <div className="my-2">
      <RawBlock
        data={message}
        label={getLabel()}
        defaultCollapsed={isLarge}
      />
    </div>
  );
}

export default memo(RawMessageView);
